import React, { useState, useEffect } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { Box, Grid, Breadcrumbs } from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import NfcIcon from '@mui/icons-material/Nfc';
import Dropdown from '../../../components/Dropdown'
import TextField from "../../../components/TextField";
import GridTable from "../../../components/GridTable";
import ActionMenu from "../../../components/ActionMenu";
import Dialog from "../../../components/Dialog";
import Loader from "../../../components/Loader";
import Button from "../../../components/Button";
import { successMessage, errorMessage } from "../../../components/Toaster";
import {
    getMetadataByCompanyId,
    createMetadata,
    updateMetadata,
    deleteMetadata,
    getMetadataTypes
} from "./api";
import {
    MetadataData,
    CreateMetadataParams,
    DeleteMetadataParams,
    ApiResponse,
} from "./interface";
import { getErrorMessage } from "../../../utils/error";
import { GridColDef, GridCellParams } from "@mui/x-data-grid";

const Metadata: React.FC = () => {
    const { company_id } = useParams();
    const location = useLocation();
    const company_name = location.state?.company_name || "Metadata";
    const [metadataList, setMetadataList] = useState<MetadataData[]>([]);
    const [metadataTypes, setMetadataTypes] = useState<string[]>([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [editMetadata, setEditMetadata] = useState<MetadataData | null>(null);
    const [isDialogOpen, setDialogOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<GridCellParams | null>(null);
    const [loading, setLoading] = useState({ fetch: false, create: false, update: false, delete: false });

    const validationSchema = Yup.object().shape({
        metadata_key: Yup.string().required("Metadata key is required"),
        metadata_value: Yup.string().required("Metadata value is required"),
        metadata_type: Yup.string().required("Metadata type is required"),
    });

    const columns: GridColDef[] = [
        { field: "metadata_key", headerName: "Key", flex: 1 },
        { field: "metadata_value", headerName: "Value", flex: 1 },
        { field: "metadata_type", headerName: "Type", flex: 1 },
        {
            field: "actions",
            headerName: "Actions",
            flex: 1,
            sortable: false,
            filterable: false,
            renderCell: (params: GridCellParams) => (
                <ActionMenu
                    params={params}
                    actions={[
                        {
                            icon: <EditIcon color="primary" />,
                            tooltip: "Edit",
                            onClick: () => handleEdit(params),
                        },
                        {
                            icon: <DeleteIcon color="error" />,
                            tooltip: "Delete",
                            onClick: () => handleDelete(params),
                        },
                    ]}
                />
            ),
        },
    ];

    useEffect(() => {
        const fetchMetadata = async () => {
            if (!company_id) return;
            setLoading((prev) => ({ ...prev, fetch: true }));
            try {
                const res: ApiResponse<MetadataData[]> = await getMetadataByCompanyId(company_id);
                if (res.status && res.data) {
                    setMetadataList(res.data);
                } else {
                    errorMessage(res.message || "Failed to fetch metadata.");
                }
            } catch (err) {
                errorMessage(getErrorMessage(err));
            } finally {
                setLoading((prev) => ({ ...prev, fetch: false }));
            }
        };
        fetchMetadata();
    }, [company_id]);

    const openDialog = async () => {
        setDialogOpen(true);
        setLoading((prev) => ({ ...prev, fetch: true }));
        try {
            const metadataTypesResponse: ApiResponse<string[]> = await getMetadataTypes();
            if (metadataTypesResponse.status && metadataTypesResponse?.data) {
                setMetadataTypes(metadataTypesResponse?.data);
            } else {
                errorMessage(metadataTypesResponse.message || "Failed to fetch metadata types.");
            }
        } catch (error) {
            console.error("Error fetching metadata types:", error);
            const message = getErrorMessage(error);
            errorMessage(message || "Error fetching metadata types");
        } finally {
            setLoading((prev) => ({ ...prev, fetch: false }));
        }
    };

    const closeDialog = (resetForm: () => void) => {
        resetForm();
        setEditMetadata(null);
        setDialogOpen(false);
    };

    const handleEdit = (params: GridCellParams) => {
        setEditMetadata(params.row as MetadataData);
        openDialog();
    };

    const handleDelete = (params: GridCellParams) => {
        setItemToDelete(params);
        setDeleteOpen(true);
    };

    const confirmDialog = async (
        values: { metadata_key: string; metadata_value: string; metadata_type: string },
        { resetForm }: { resetForm: () => void }
    ) => {
        let success = false;
        if (editMetadata) {
            success = await handleUpdate(values);
        } else {
            success = await handleCreate(values);
        }
        if (success) {
            closeDialog(resetForm);
        }
    };

    const handleCreate = async (values: any) => {
        setLoading((prev) => ({ ...prev, create: true }));
        try {
            const payload: CreateMetadataParams = {
                company_id: company_id!,
                metadata_key: values.metadata_key,
                metadata_value: values.metadata_value,
                metadata_type: values.metadata_type,
            };
            const res = await createMetadata(payload);
            if (res.status) {
                setMetadataList((prev) => [...prev, { ...payload }]);
                successMessage(res.message);
                return true;
            } else {
                errorMessage(res.message || "Failed to create metadata.");
                return false;
            }
        } catch (err) {
            errorMessage(getErrorMessage(err));
            return false;
        } finally {
            setLoading((prev) => ({ ...prev, create: false }));
        }
    };

    const handleUpdate = async (values: any) => {
        setLoading((prev) => ({ ...prev, update: true }));
        try {
            const payload = {
                company_id: company_id!,
                metadata_key: values.metadata_key,
                metadata_value: values.metadata_value,
                metadata_type: values.metadata_type,
            };
            const res = await updateMetadata(payload);
            if (res.status) {
                setMetadataList((prev) =>
                    prev.map((meta) =>
                        meta.metadata_key === payload.metadata_key ? { ...meta, ...payload } : meta
                    )
                );
                successMessage(res.message);
                return true;
            } else {
                errorMessage(res.message || "Failed to update metadata.");
                return false;
            }
        } catch (err) {
            errorMessage(getErrorMessage(err));
            return false;
        } finally {
            setLoading((prev) => ({ ...prev, update: false }));
        }
    };

    const handleDeleteConfirm = async () => {
        setLoading((prev) => ({ ...prev, delete: true }));
        try {
            if (itemToDelete) {
                const { metadata_key } = itemToDelete.row;
                const payload: DeleteMetadataParams = {
                    company_id: company_id!,
                    metadata_key: metadata_key,
                };
                const res = await deleteMetadata(payload);
                if (res.status) {
                    setMetadataList((prev) =>
                        prev.filter((meta) => meta.metadata_key !== metadata_key)
                    );
                    successMessage(res.message);
                } else {
                    errorMessage(res.message);
                }
            }
        } catch (err) {
            errorMessage(getErrorMessage(err));
        } finally {
            setLoading((prev) => ({ ...prev, delete: false }));
            setDeleteOpen(false);
        }
    };

    return (
        <>
            {loading.fetch ? (
                <Loader color="primary" size={40} />
            ) : (
                <Box className="m-a-20">
                    <Breadcrumbs
                        separator={<NavigateNextIcon fontSize="small" />}
                        aria-label="breadcrumb"
                        style={{ fontSize: "18px", fontWeight: "bold", marginBottom: "30px" }}
                    >
                        <Link to="/company">Company</Link>
                        <Link to={`/company`}>
                            {company_name}
                        </Link>
                        <Link to={`/company/${company_id}/metadata`} state={{ company_name }}>
                            Metadata
                        </Link>
                    </Breadcrumbs>
                    <div className="d-flex justify-between w-100">
                        <TextField
                            className="w-25"
                            label="Search"
                            variant="outlined"
                            value={searchQuery}
                            size="small"
                            startIcon={<SearchIcon />}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <Button
                            label="Add"
                            variant="contained"
                            color="primary"
                            onClick={openDialog}
                        />
                    </div>
                    <Box className="ag-theme-alpine m-t-20" style={{ height: "100%", width: "100%" }}>
                        <GridTable
                            rows={metadataList}
                            columns={columns}
                            pageSize={10}
                            searchQuery={searchQuery}
                            setSearchQuery={setSearchQuery}
                            getRowId={(row) => row.metadata_key}
                        />
                    </Box>
                    <Formik
                        initialValues={{
                            metadata_key: editMetadata?.metadata_key || "",
                            metadata_value: editMetadata?.metadata_value || "",
                            metadata_type: editMetadata?.metadata_type || "STRING",
                        }}
                        validationSchema={validationSchema}
                        onSubmit={confirmDialog}
                        enableReinitialize
                    >
                        {({ values, handleChange, handleSubmit, errors, touched, resetForm, setFieldValue }) => (
                            <Dialog
                                open={isDialogOpen}
                                onClose={() => closeDialog(resetForm)}
                                onConfirm={handleSubmit}
                                title={editMetadata ? "Edit Metadata" : "Add New Metadata"}
                                confirmButtonText={editMetadata ? "Update" : "Add"}
                                cancelButtonText="Cancel"
                                icon={<NfcIcon />}
                                message={
                                    <Box padding={4}>
                                        {(loading.create || loading.update) ? (
                                            <Loader color="primary" size={40} />
                                        ) : (
                                            <Form>
                                                <Grid container spacing={2}>
                                                    <Grid size={4}>
                                                        <TextField
                                                            id="metadata_key"
                                                            name="metadata_key"
                                                            label="Key"
                                                            fullWidth
                                                            required
                                                            disabled={!!editMetadata}
                                                            value={values.metadata_key}
                                                            onChange={handleChange}
                                                            error={Boolean(touched.metadata_key && errors.metadata_key)}
                                                            helperText={touched.metadata_key && errors.metadata_key}
                                                        />
                                                    </Grid>
                                                    <Grid size={4}>
                                                        <TextField
                                                            id="metadata_value"
                                                            name="metadata_value"
                                                            label="Value"
                                                            fullWidth
                                                            value={values.metadata_value}
                                                            onChange={handleChange}
                                                            error={Boolean(touched.metadata_value && errors.metadata_value)}
                                                            helperText={touched.metadata_value && errors.metadata_value}
                                                        />
                                                    </Grid>
                                                    <Grid size={4}>
                                                        {metadataTypes?.length > 0 && (
                                                            <Dropdown
                                                                options={metadataTypes}
                                                                value={values.metadata_type ?? ""}
                                                                onChange={(value) => setFieldValue("metadata_type", value)}
                                                                label="Metadata Type"
                                                                width="200px"
                                                                required
                                                                error={Boolean(touched.metadata_type && errors.metadata_type)}
                                                                helperText={touched.metadata_type && errors.metadata_type ? String(errors.metadata_type) : undefined}
                                                            />
                                                        )}
                                                    </Grid>
                                                </Grid>
                                            </Form>
                                        )}
                                    </Box>
                                }
                            />
                        )}
                    </Formik>
                </Box>
            )}
            <Dialog
                open={deleteOpen}
                onClose={() => setDeleteOpen(false)}
                onConfirm={handleDeleteConfirm}
                title="Delete Metadata"
                confirmButtonText="Confirm"
                cancelButtonText="Cancel"
                icon={<DeleteIcon />}
                message={
                    <Box padding={2}>
                        {loading.delete ? (
                            <Loader color="primary" size={40} />
                        ) : (
                            "Are you sure you want to delete this Metadata?"
                        )}
                    </Box>
                }
            />
        </>
    );
};

export default Metadata;